import { useEffect, useMemo, useState } from 'react';
import {
  FiPlus,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiFileText,
  FiEdit2,
  FiUploadCloud,
  FiDownload,
  FiUser,
  FiClock,
  FiMapPin,
  FiBriefcase,
  FiActivity,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  Input,
  Select,
  MultiSelect,
  DatePeriodFilter,
  getDateParams,
} from '../../components';
import '../CompanyManager/style.css';
import TableActions from '../../components/Table/TableActions';

import './style.css';
import { cidsRaw } from '../../constants/cids';

function buildCompanyOptions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((c) => ({
    value: String(c.id),
    label:
      c.corporate_name ||
      c.trade_name ||
      c.name ||
      `Empresa ${c.id}`,
  }));
}

function normalizeSignatureSrc(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  if (!value) return null;
  if (value.startsWith('data:')) return value;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `data:image/png;base64,${value}`;
}

function getAbsenceWorkflowStageInfo(absence) {
  const supervisorStatus = absence?.supervisor_status || 'pending';
  if (supervisorStatus === 'rejected') {
    return {
      label: 'Rejeitada pelo supervisor',
      variant: 'danger',
      hideMainStatus: true,
    };
  }

  const hasSubstitute =
    !!absence?.substitute_employee_id || !!absence?.substitute_employee;
  if (!hasSubstitute) return null;

  const substituteStatus = absence?.substitute_status || 'pending';
  const mainStatus = absence?.status ? String(absence.status) : '';

  if (substituteStatus === 'rejected') {
    return null;
  }

  if (substituteStatus === 'pending') {
    if (
      ['rejeitada', 'rejected_by_substitute', 'reprovada', 'reprovado'].includes(
        mainStatus
      )
    ) {
      return null;
    }
    return {
      label: 'Pendente assinatura do substituto',
      variant: 'warning',
      hideMainStatus: true,
    };
  }

  if (substituteStatus === 'accepted' && supervisorStatus === 'pending') {
    return {
      label: 'Pendente aprovação do supervisor',
      variant: 'warning',
      hideMainStatus: true,
    };
  }

  return null;
}

export default function AbsenceManager() {
  const permissions = usePermission('time_keeping');
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);

  const [serviceStations, setServiceStations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [reasons, setReasons] = useState([]);

  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedServiceStation, setSelectedServiceStation] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedReason, setSelectedReason] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [periodMode, setPeriodMode] = useState('monthly');
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [absenceToDelete, setAbsenceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();
  const [companyFilterInitialized, setCompanyFilterInitialized] = useState(false);

  useEffect(() => {
    if (permissions.canView) {
      (async () => {
        try {
          const data = await Api.getCompanies();
          const list = Array.isArray(data) ? data : data?.data || [];
          setCompanies(list);
          setSelectedCompanyId('all');
        } catch (err) {
          toast.error('Erro ao carregar empresas.');
        }
      })();
    }
  }, [permissions.canView]);

  useEffect(() => {
    if (!permissions.canView) return;
    if (!selectedCompanyId || companies.length === 0) return;

    const allValue = 'all';
    const selected =
      selectedCompanyId === 'all'
        ? null
        : companies.find((c) => String(c.id) === String(selectedCompanyId));

    const baseCompanies = buildCompanyOptions(companies);
    const defaultValues =
      selectedCompanyId === 'all'
        ? [allValue, ...baseCompanies.map((c) => c.value)]
        : [String(selectedCompanyId)];

    if (!companyFilterInitialized && (!companyFilterValues || companyFilterValues.length === 0)) {
      setCompanyFilterValues(defaultValues);
      setCompanyFilterInitialized(true);
    }

    if (selectedCompanyId === 'all') {
      const ids = companies.map((c) => c.id);
      Promise.all(ids.map((id) => Api.getServiceStations(id)))
        .then((res) => {
          const merged = res.flat();
          if (Array.isArray(merged)) {
            setServiceStations(
              merged.map((s) => ({
                value: String(s.id),
                label: s.name,
                companyId: String(s.company_id),
              }))
            );
          }
        })
        .catch(console.error);

      Promise.all(ids.map((id) => Api.getEmployees(id)))
        .then((res) => {
          const merged = res.flat();
          if (Array.isArray(merged)) {
            setEmployees(
              merged.map((e) => ({
                value: String(e.id),
                label: e.name,
                companyId: String(e.company_id),
                serviceStationIds: Array.isArray(e.serviceStations)
                  ? e.serviceStations.map((s) => String(s.id))
                  : [],
                status: e.status,
                is_active: e.is_active,
              }))
            );
          }
        })
        .catch(console.error);

      Promise.all(ids.map((id) => Api.getReasons(id)))
        .then((res) => {
          const merged = res.flat();
          if (Array.isArray(merged)) {
            setReasons(
              merged.map((r) => ({
                value: String(r.id),
                label: r.name,
                companyId: r.company_id,
              }))
            );
          }
        })
        .catch(console.error);
    } else {
      Api.getServiceStations(selectedCompanyId)
        .then((data) => {
          if (Array.isArray(data)) {
            setServiceStations(
              data.map((s) => ({
                value: String(s.id),
                label: s.name,
                companyId: String(s.company_id || selectedCompanyId),
              }))
            );
          }
        })
        .catch(console.error);

      Api.getEmployees(selectedCompanyId)
        .then((data) => {
          if (Array.isArray(data)) {
            setEmployees(
              data.map((e) => ({
                value: String(e.id),
                label: e.name,
                companyId: String(e.company_id),
                serviceStationIds: Array.isArray(e.serviceStations)
                  ? e.serviceStations.map((s) => String(s.id))
                  : [],
                status: e.status,
                is_active: e.is_active,
              }))
            );
          }
        })
        .catch(console.error);

      Api.getReasons(selectedCompanyId)
        .then((data) => {
          if (Array.isArray(data)) {
            setReasons(
              data.map((r) => ({
                value: String(r.id),
                label: r.name,
                companyId: String(r.company_id || selectedCompanyId),
              }))
            );
          }
        })
        .catch(console.error);
    }

    loadAbsences(selectedCompanyId);
  }, [selectedCompanyId, companies, periodMode, currentDate, companyFilterInitialized, companyFilterValues]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, selectedServiceStation, selectedEmployee, selectedReason, companyFilterValues]);

  async function loadAbsences(companyId) {
    if (!permissions.canView) return;
    try {
      setLoading(true);
      const { startDate, endDate } = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);

      if (companyId === 'all') {
        const ids = companies.map((c) => c.id);
        const responses = await Promise.all(
          ids.map((id) =>
            Api.getAbsences(id, {
              startDate,
              endDate,
            })
          )
        );
        const merged = responses.flatMap((r) =>
          Array.isArray(r) ? r : r?.data || []
        );
        setAbsences(merged);
      } else {
        const data = await Api.getAbsences(companyId, {
          startDate,
          endDate,
        });
        const list = Array.isArray(data) ? data : data?.data || [];
        setAbsences(list);
      }
      setHasLoadedOnce(true);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar ausências. Tente novamente.');
      setAbsences([]);
    } finally {
      setLoading(false);
    }
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map((c) => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter((v) => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter((v) => v !== allValue);
      const allIds = companies.map((c) => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter((v) => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setSelectedCompanyId(idsWithoutAll[0]);
    } else {
      setSelectedCompanyId('all');
    }
  }

  function handleCreate() {
    setSelectedAbsence(null);
    setIsModalOpen(true);
  }

  function handleEdit(absence) {
    setSelectedAbsence(absence);
    setIsModalOpen(true);
  }

  function handleFormSuccess() {
    setIsModalOpen(false);
    loadAbsences(selectedCompanyId);
  }

  function handleDelete(absence) {
    setAbsenceToDelete(absence);
    setIsDeleteModalOpen(true);
  }

  function handleCancelDelete() {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setAbsenceToDelete(null);
  }

  async function handleConfirmDelete() {
    if (!absenceToDelete) return;

    try {
      setIsDeleting(true);
      await Api.deleteAbsence(
        absenceToDelete.company_id || selectedCompanyId,
        absenceToDelete.id
      );
      loadAbsences(selectedCompanyId);
      setIsDeleteModalOpen(false);
      setAbsenceToDelete(null);
      toast.success('Ausência excluída com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao excluir ausência.');
    } finally {
      setIsDeleting(false);
    }
  }

  const stationFilterOptions = useMemo(() => {
    const allValue = 'all';
    const idsWithoutAll = companyFilterValues.filter((v) => v !== allValue);
    if (idsWithoutAll.length === 0) return serviceStations;
    return serviceStations.filter((s) => {
      if (!s.companyId) return true;
      return idsWithoutAll.includes(String(s.companyId));
    });
  }, [serviceStations, companyFilterValues]);

  const filteredAbsences = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const { startDate, endDate } = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
    const allValue = 'all';
    const specificIds = companyFilterValues.filter((v) => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return absences.filter((item) => {
      if (startDate && endDate) {
        const createdAtStr = item.createdAt ? String(item.createdAt) : null;
        const createdAtDate =
          createdAtStr && createdAtStr.length >= 10
            ? createdAtStr.slice(0, 10)
            : null;
        if (!createdAtDate) {
          return false;
        }
        if (createdAtDate < startDate || createdAtDate > endDate) {
          return false;
        }
      }

      if (term) {
        const employeeName = (item.employee && item.employee.name) || '';
        const observation = item.observation || '';
        const reasonName = (item.reason && item.reason.name) || '';
        if (
          !employeeName.toLowerCase().includes(term) &&
          !observation.toLowerCase().includes(term) &&
          !reasonName.toLowerCase().includes(term)
        ) {
          return false;
        }
      }

      if (statusFilter && statusFilter !== 'all') {
        if (item.status !== statusFilter) {
          return false;
        }
      }

      if (selectedServiceStation) {
        if (String(item.service_station_id) !== String(selectedServiceStation)) {
          return false;
        }
      }

      if (selectedEmployee) {
        if (String(item.employee_id) !== String(selectedEmployee)) {
          return false;
        }
      }

      if (selectedReason) {
        if (String(item.reason_id) !== String(selectedReason)) {
          return false;
        }
      }

      if (!hasAllSelected && specificIds.length > 0) {
        if (!specificIds.includes(String(item.company_id))) {
          return false;
        }
      }

      return true;
    });
  }, [
    absences,
    searchTerm,
    statusFilter,
    selectedServiceStation,
    selectedEmployee,
    selectedReason,
    companyFilterValues,
    periodMode,
    currentDate,
  ]);

  const totalItems = filteredAbsences.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentItems = filteredAbsences.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const companyOptions = [
    { value: 'all', label: 'TODAS' },
    ...buildCompanyOptions(companies),
  ];

  const reasonFilterOptions = useMemo(() => {
    const base = [{ value: '', label: 'TODOS' }];
    if (!Array.isArray(reasons) || reasons.length === 0) return base;

    const hasMultipleCompanies = Array.isArray(companies) && companies.length > 1;

    return [
      ...base,
      ...reasons.map((r) => {
        if (!hasMultipleCompanies) {
          return r;
        }

        const companyId = r.companyId || r.company_id;
        const company = companies.find(
          (c) => String(c.id) === String(companyId)
        );

        if (!company) {
          return r;
        }

        const companyName =
          company.corporate_name ||
          company.trade_name ||
          company.name ||
          `Empresa ${company.id}`;

        return {
          ...r,
          label: `${r.label} - ${companyName}`,
        };
      }),
    ];
  }, [reasons, companies]);

  const statusOptions = [
    { value: 'all', label: 'TODOS' },
    { value: 'registrada', label: 'Registrada' },
    { value: 'aprovada', label: 'Aprovada' },
    { value: 'rejeitada', label: 'Rejeitada' },
    { value: 'rejected_by_substitute', label: 'Rejeitada p/ Substituto' },
  ];

  const pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiActivity size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && (
          (() => {
            const { CompanyManagerSkeleton } = Skeletons;
            return <CompanyManagerSkeleton />;
          })()
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiFileText size={22} />
                </div>
                <div>
                  <h1>Lançamentos</h1>
                  <p>
                    Registre e acompanhe lançamentos dos colaboradores com observações e anexos.
                  </p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button
                  onClick={handleCreate}
                  className="manager-header-button"
                >
                  <FiPlus size={18} />
                  Lançamento
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {absences.length > 0 && (
                  <span className="manager-filters-count">
                    {totalItems} de {absences.length} ausências
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <DatePeriodFilter
                    periodMode={periodMode}
                    setPeriodMode={setPeriodMode}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    customRange={customPeriod}
                    setCustomRange={setCustomPeriod}
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="company-filter"
                    options={companyOptions}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Posto de Serviço</label>
                  <Select
                    value={selectedServiceStation}
                    onChange={(e) => {
                      setSelectedServiceStation(e.target.value);
                    }}
                    options={[
                      { value: '', label: 'TODOS' },
                      ...stationFilterOptions,
                    ]}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Colaborador</label>
                  <Select
                    value={selectedEmployee}
                    onChange={(e) => {
                      setSelectedEmployee(e.target.value);
                    }}
                    options={[
                      { value: '', label: 'TODOS' },
                      ...employees,
                    ]}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Motivo</label>
                  <Select
                    value={selectedReason}
                    onChange={(e) => {
                      setSelectedReason(e.target.value);
                    }}
                    options={reasonFilterOptions}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Status</label>
                  <Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                    }}
                    options={statusOptions}
                    searchable={false}
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="absence-search"
                      placeholder="Busque por colaborador, motivo ou observação"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!error && absences.length === 0 && !loading && (
              <div className="empty-state">
                <p>Nenhuma ausência cadastrada.</p>
              </div>
            )}

            {!error && absences.length > 0 && (
              <Table
                items={currentItems}
                columns={[
                  {
                    id: 'employee',
                    header: 'Colaborador',
                    width: '2.2fr',
                    cellClassName: 'manager-table-cell-primary',
                    render: (item) => (
                      <>
                        <div className="manager-table-avatar">
                          <FiUser size={18} />
                        </div>
                        <div className="manager-table-cell-text">
                          <span className="manager-table-title">
                            {item.employee?.name || 'Sem nome'}
                          </span>
                          {(() => {
                            const formatDate = (value) => {
                              if (!value) return '';
                              const str = String(value);
                              if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
                                const [year, month, day] = str.split('-');
                                return `${day.padStart(2, '0')}/${month.padStart(
                                  2,
                                  '0'
                                )}/${year}`;
                              }
                              return str;
                            };

                            const startDateLabel = formatDate(item.date);
                            const startTimeLabel = item.start_time
                              ? String(item.start_time).slice(0, 5)
                              : '';
                            const endDateLabel = formatDate(item.end_date);
                            const endTimeLabel = item.end_time
                              ? String(item.end_time).slice(0, 5)
                              : '';

                            const hasStart = startDateLabel || startTimeLabel;
                            const hasEnd = endDateLabel || endTimeLabel;

                            if (!hasStart && !hasEnd) return null;

                            return (
                              <span className="manager-table-subtitle absence-time-row">
                                <FiClock size={12} />
                                <span>
                                  {startDateLabel || 'Sem data'}
                                  {startTimeLabel ? ` ${startTimeLabel}` : ''}
                                  {hasEnd && (
                                    <>
                                      {'  '}
                                      →
                                      {'  '}
                                      {endDateLabel || startDateLabel}
                                      {endTimeLabel ? ` ${endTimeLabel}` : ''}
                                    </>
                                  )}
                                </span>
                              </span>
                            );
                          })()}
                          {item.substitute_employee && (
                            <span className="manager-table-subtitle">
                              Substituto: {item.substitute_employee}
                            </span>
                          )}
                        </div>
                      </>
                    ),
                  },
                  {
                    id: 'company',
                    header: 'Empresa/Posto',
                    width: '1.8fr',
                    render: (item) => {
                      const company =
                        companies.find((c) => String(c.id) === String(item.company_id)) ||
                        companies.find((c) => String(c.id) === String(selectedCompanyId));
                      const companyLabel =
                        company?.corporate_name ||
                        company?.trade_name ||
                        company?.name ||
                        (company ? `Empresa ${company.id}` : '—');
                      const station =
                        serviceStations.find(
                          (s) => String(s.value) === String(item.service_station_id)
                        ) || null;
                      const stationLabel = station?.label || null;
                      return (
                        <div className="manager-table-cell-text">
                          <div className="manager-table-text-with-icon">
                            <FiBriefcase size={14} style={{ marginRight: 6 }} />
                            <span>{companyLabel}</span>
                          </div>
                          {stationLabel && (
                            <span className="manager-table-subtitle manager-table-text-with-icon">
                              <FiMapPin size={14} style={{ marginRight: 4 }} />
                              <span>{stationLabel}</span>
                            </span>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    id: 'reason',
                    header: 'Motivo',
                    render: (item) => item.reason?.name || '',
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    width: '1fr',
                    cellClassName: 'manager-table-cell manager-table-col-tags',
                    render: (item) => {
                      const statusValue = item.status || 'registrada';
                      let variant = 'neutral';

                      if (statusValue === 'aprovada') {
                        variant = 'success';
                      } else if (
                        statusValue === 'rejeitada' ||
                        statusValue === 'rejected_by_substitute'
                      ) {
                        variant = 'danger';
                      } else {
                        variant = 'info';
                      }

                      const labelMap = {
                        registrada: 'Registrada',
                        aprovada: 'Aprovada',
                        rejeitada: 'Rejeitada',
                        pendente: 'Pendente',
                        pending: 'Pendente',
                        rejected_by_substitute: 'Rejeitada p/ Substituto',
                      };

                      const label = labelMap[statusValue] || statusValue;
                      const stageInfo = getAbsenceWorkflowStageInfo(item);
                      const hideMainStatus = !!stageInfo?.hideMainStatus;

                      return (
                        <div className="manager-tags">
                          {!hideMainStatus && (
                            <span
                              className={[
                                'manager-tag',
                                variant === 'success' && 'manager-tag-success',
                                variant === 'warning' && 'manager-tag-warning',
                                variant === 'danger' && 'manager-tag-danger',
                                variant === 'info' && 'manager-tag-info',
                                variant === 'neutral' && 'manager-tag-neutral',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            >
                              {label}
                            </span>
                          )}
                          {stageInfo && (
                            <span
                              className={[
                                'manager-tag',
                                stageInfo.variant === 'success' && 'manager-tag-success',
                                stageInfo.variant === 'warning' && 'manager-tag-warning',
                                stageInfo.variant === 'danger' && 'manager-tag-danger',
                                stageInfo.variant === 'info' && 'manager-tag-info',
                                stageInfo.variant === 'neutral' && 'manager-tag-neutral',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            >
                              {stageInfo.label}
                            </span>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    id: 'created_at',
                    header: 'Solicitação',
                    width: '1.4fr',
                    render: (item) => {
                      let dateLabel = '';
                      if (item.createdAt) {
                        const d = new Date(item.createdAt);
                        if (!Number.isNaN(d.getTime())) {
                          dateLabel = d.toLocaleDateString('pt-BR');
                        }
                      }

                      console.log(item.created_by_name )

                      const creator = item.created_by_name || null;
                      const initial = creator
                        ? creator.trim().charAt(0).toUpperCase()
                        : '';

                      return (
                        <div className="absence-created-at">
                          <div className="absence-created-at-date">
                            {dateLabel || '—'}
                          </div>
                          {creator && (
                            <div className="absence-created-at-user">
                              <div className="manager-table-avatar">
                                <span>{initial}</span>
                              </div>
                              <div className="absence-created-at-user-info">
                                <span className="absence-created-at-user-label">
                                  Criado por
                                </span>
                                <span className="absence-created-at-user-name">
                                  {creator}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    id: 'actions',
                    header: 'Ações',
                    width: '0.6fr',
                    cellClassName: 'manager-table-cell manager-table-col-actions',
                    render: (item) => (
                      <TableActions>
                        {permissions.canEdit && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item);
                            }}
                          >
                            <FiEdit2 size={16} />
                            Editar
                          </button>
                        )}
                        {permissions.canDelete && (
                          <button
                            type="button"
                            className="action-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item);
                            }}
                          >
                            <FiTrash2 size={16} />
                            Excluir
                          </button>
                        )}
                      </TableActions>
                    ),
                  },
                ]}
                totalItems={totalItems}
                page={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={(item) => item.id}
                emptyMessage="Nenhuma ausência encontrada."
                onRowDoubleClick={handleEdit}
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedAbsence ? 'Editar lançamento' : 'Novo lançamento'}
        size="xl"
        closeOnOverlayClick={false}
      >
        <AbsenceForm
          initialData={selectedAbsence}
          companies={companies}
          employees={employees}
          serviceStations={serviceStations}
          reasons={reasons}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir o lançamento de{' '}
            {absenceToDelete && (
              <span className="delete-modal-company-name">
                {absenceToDelete.employee?.name}
              </span>
            )}
            ?
          </p>
          <p>Essa ação não poderá ser desfeita.</p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}

function formatCurrencyForDisplay(raw) {
  if (raw === undefined || raw === null || raw === '') {
    return '';
  }

  let value = String(raw).replace(/[^\d,.-]/g, '').replace(',', '.');
  const num = Number(value);

  if (Number.isNaN(num)) {
    return '';
  }

  const fixed = num.toFixed(2).replace('.', ',');
  const parts = fixed.split(',');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decimalPart = parts[1];

  return `R$ ${integerPart},${decimalPart}`;
}

function AbsenceForm({
  initialData,
  companies,
  employees,
  serviceStations,
  reasons,
  onSuccess,
  onCancel,
}) {
  const [selectedCompany, setSelectedCompany] = useState(() => {
    if (initialData?.company_id) return String(initialData.company_id);
    if (initialData?.company?.id) return String(initialData.company.id);
    return '';
  });
  const [employeeId, setEmployeeId] = useState(
    initialData?.employee_id ? String(initialData.employee_id) : ''
  );
  const [serviceStationId, setServiceStationId] = useState(
    initialData?.service_station_id
      ? String(initialData.service_station_id)
      : ''
  );
  const [reasonId, setReasonId] = useState(
    initialData?.reason_id ? String(initialData.reason_id) : ''
  );
  const [date, setDate] = useState(() => {
    if (initialData?.date) {
      const str = String(initialData.date);
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [year, month, day] = str.split('-');
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      }
      return str;
    }
    return '';
  });
  const [endDate, setEndDate] = useState(() => {
    if (initialData?.end_date) {
      const str = String(initialData.end_date);
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [year, month, day] = str.split('-');
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      }
      return str;
    }
    return '';
  });
  const [startTime, setStartTime] = useState(
    initialData?.start_time || ''
  );
  const [endTime, setEndTime] = useState(initialData?.end_time || '');
  const [status, setStatus] = useState(initialData?.status || 'aprovada');
  const [supervisorRejectionReason, setSupervisorRejectionReason] = useState(
    initialData?.supervisor_rejection_reason || ''
  );
  const [observation, setObservation] = useState(
    initialData?.observation || ''
  );

  const [hasSubstitute, setHasSubstitute] = useState(() => {
    if (initialData?.substitute_employee_id || initialData?.substitute_employee) {
      return 'yes';
    }
    return 'no';
  });

  const [substituteEmployee, setSubstituteEmployee] = useState(
    initialData?.substitute_employee || ''
  );

  const [substituteEmployeeId, setSubstituteEmployeeId] = useState(
    initialData?.substitute_employee_id
      ? String(initialData.substitute_employee_id)
      : ''
  );

  const [substituteIsExtra, setSubstituteIsExtra] = useState(() => {
    if (initialData?.substitute_is_extra === true) return 'yes';
    if (initialData?.substitute_is_extra === false) return 'no';
    return '';
  });
  const [substituteDayValue, setSubstituteDayValue] = useState(
    formatCurrencyForDisplay(initialData?.substitute_daily_value)
  );
  const [substituteAdvanceValue, setSubstituteAdvanceValue] = useState(
    formatCurrencyForDisplay(initialData?.substitute_advance_value)
  );
  const [substituteVaDayValue, setSubstituteVaDayValue] = useState(
    formatCurrencyForDisplay(initialData?.substitute_va_day_value)
  );
  const [substituteVtDayValue, setSubstituteVtDayValue] = useState(
    formatCurrencyForDisplay(initialData?.substitute_vt_day_value)
  );
  const [substituteInvoiceValue, setSubstituteInvoiceValue] = useState(
    formatCurrencyForDisplay(initialData?.substitute_invoice_value)
  );

  const [cidCode, setCidCode] = useState(initialData?.cid_code || '');
  const [doctorName, setDoctorName] = useState(initialData?.doctor_name || '');
  const [doctorCrm, setDoctorCrm] = useState(initialData?.doctor_crm || '');

  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState(
    initialData?.attachments || []
  );

  const [submitting, setSubmitting] = useState(false);

  const companyOptions = buildCompanyOptions(companies);
  const employeeOptions = employees;
  const employeeSignatureSrc = useMemo(
    () => normalizeSignatureSrc(initialData?.signature_image),
    [initialData?.signature_image]
  );
  const substituteSignatureSrc = useMemo(
    () => normalizeSignatureSrc(initialData?.substitute_signature_image),
    [initialData?.substitute_signature_image]
  );
  const stationOptions = useMemo(() => {
    if (!selectedCompany) return serviceStations;
    return serviceStations.filter((s) => {
      if (!s.companyId) return true;
      return String(s.companyId) === String(selectedCompany);
    });
  }, [serviceStations, selectedCompany]);
  const reasonOptions = reasons.filter(
    (r) => !selectedCompany || String(r.companyId) === String(selectedCompany)
  );
  const cidOptions = useMemo(() => {
    const map = new Map();
    const lines = cidsRaw.split('\n').map((l) => l.trim()).filter(Boolean);
    const re = /^([A-Z][0-9]{2}(?:\.[0-9])?)\s+(.+)$/;
    for (const line of lines) {
      const m = line.match(re);
      if (!m) continue;
      const code = m[1];
      const desc = m[2];
      if (!map.has(code)) {
        map.set(code, { value: code, label: `${code} - ${desc}` });
      }
    }
    const arr = Array.from(map.values());
    if (cidCode && !map.has(cidCode)) {
      arr.unshift({ value: cidCode, label: cidCode });
    }
    return arr;
  }, [cidsRaw, cidCode]);

  const stageInfo = useMemo(() => {
    const hasSelectedSubstitute =
      hasSubstitute === 'yes' && !!substituteEmployeeId;
    const baseHasSubstitute =
      !!initialData?.substitute_employee_id || !!initialData?.substitute_employee;

    if (!hasSelectedSubstitute && !baseHasSubstitute) return null;

    const substituteStatus = initialData?.substitute_status || 'pending';
    const supervisorStatus = initialData?.supervisor_status || 'pending';

    return getAbsenceWorkflowStageInfo({
      substitute_employee_id: hasSelectedSubstitute ? substituteEmployeeId : initialData?.substitute_employee_id,
      substitute_employee: initialData?.substitute_employee,
      substitute_status: substituteStatus,
      supervisor_status: supervisorStatus,
      status: initialData?.status || status,
    });
  }, [
    hasSubstitute,
    substituteEmployeeId,
    initialData?.substitute_employee_id,
    initialData?.substitute_employee,
    initialData?.substitute_status,
    initialData?.supervisor_status,
    initialData?.status,
    status,
  ]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAttachments((prev) => [...prev, ...files]);
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = async (attachmentId) => {
    try {
      if (!window.confirm('Deseja excluir este anexo permanentemente?')) return;

      await Api.deleteAbsenceAttachment(
        selectedCompany || initialData.company_id,
        initialData.id,
        attachmentId
      );

      setExistingAttachments((prev) =>
        prev.filter((a) => a.id !== attachmentId)
      );
      toast.success('Anexo excluído!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao excluir anexo.');
    }
  };

  const parseDate = (value) => {
    if (!value) return null;
    const parts = value.split('/');
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map((p) => p.padStart(2, '0'));
    return `${year}-${month}-${day}`;
  };

  const parseMoney = (value) => {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'number') return value;

    let normalized = String(value)
      .replace(/[^\d,.-]/g, '')
      .replace(/\./g, '')
      .replace(',', '.');

    const num = Number(normalized);
    if (Number.isNaN(num)) return null;
    return num;
  };

  const normalizeText = (value) => {
    if (!value) return '';
    return value
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  const inferEmployeeStatusFromContext = () => {
    const reasonOption = reasonOptions.find(
      (r) => String(r.value) === String(reasonId)
    );
    const reasonText = reasonOption?.label || '';
    const combined = `${reasonText} ${observation || ''}`;
    const text = normalizeText(combined);

    if (!text) return null;

    if (text.includes('atestad')) {
      return 'Atestado Médico';
    }

    if (
      text.includes('afastado inss') ||
      text.includes('afastamento inss') ||
      (text.includes('inss') && text.includes('afast'))
    ) {
      return 'Afastado INSS';
    }

    if (text.includes('feria')) {
      return 'Férias';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCompany) {
      toast.error('Selecione uma empresa.');
      return;
    }

    if (!employeeId) {
      toast.error('Selecione um colaborador.');
      return;
    }

    if (!date) {
      toast.error('Informe a data da ausência.');
      return;
    }

    if (!startTime) {
      toast.error('Informe a hora inicial da ausência.');
      return;
    }

    try {
      setSubmitting(true);

      const inferredStatus = inferEmployeeStatusFromContext();

      const selectedSubstitute = employees.find(
        (item) =>
          String(item.value) === String(substituteEmployeeId)
      );

      const effectiveHasSubstitute =
        hasSubstitute === 'yes' && substituteEmployeeId;

      const payload = {
        company_id: selectedCompany,
        employee_id: employeeId,
        service_station_id: serviceStationId || null,
        date: parseDate(date),
        end_date: endDate ? parseDate(endDate) : null,
        start_time: startTime || null,
        end_time: endTime || null,
        status: status || 'registrada',
        supervisor_rejection_reason:
          initialData && status === 'rejeitada'
            ? supervisorRejectionReason || null
            : null,
        reason_id: reasonId || null,
        observation: observation || null,
        cid_code: cidCode || null,
        doctor_name: doctorName || null,
        doctor_crm: doctorCrm || null,
        substitute_employee: effectiveHasSubstitute
          ? (selectedSubstitute?.label || substituteEmployee || null)
          : null,
        substitute_employee_id: effectiveHasSubstitute
          ? Number(substituteEmployeeId)
          : null,
        substitute_is_extra: effectiveHasSubstitute
          ? (substituteIsExtra === ''
              ? null
              : substituteIsExtra === 'yes')
          : null,
        substitute_daily_value: effectiveHasSubstitute
          ? parseMoney(substituteDayValue)
          : null,
        substitute_advance_value: effectiveHasSubstitute
          ? parseMoney(substituteAdvanceValue)
          : null,
        substitute_va_day_value: effectiveHasSubstitute
          ? parseMoney(substituteVaDayValue)
          : null,
        substitute_vt_day_value: effectiveHasSubstitute
          ? parseMoney(substituteVtDayValue)
          : null,
        substitute_invoice_value: effectiveHasSubstitute
          ? parseMoney(substituteInvoiceValue)
          : null,
        attachments: [],
      };

      if (attachments.length > 0) {
        const toBase64 = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                file_name: file.name,
                file_type: file.type,
                file_size: file.size,
                file_data: reader.result.split(',')[1],
              });
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

        const filesData = await Promise.all(
          attachments.map((file) => toBase64(file))
        );
        payload.attachments = filesData;
      }

      if (initialData) {
        await Api.updateAbsence(selectedCompany, initialData.id, payload);
        toast.success('Ausência atualizada com sucesso!');
      } else {
        await Api.createAbsence(selectedCompany, payload);
        toast.success('Ausência criada com sucesso!');
      }

      const currentAbsenceStatus = (status || 'registrada');
      if (currentAbsenceStatus === 'aprovada' && inferredStatus) {
        const emp = employees.find(
          (item) => String(item.value) === String(employeeId)
        );
        if (emp) {
          const currentStatus =
            emp.status || (emp.is_active ? 'Ativo' : 'Inativo');
          if (currentStatus !== inferredStatus) {
            try {
              const employeeCompanyId = emp.companyId || selectedCompany;
              if (employeeCompanyId) {
                const nextIsActive = inferredStatus === 'Inativo' ? false : true;
                await Api.updateEmployee(employeeCompanyId, emp.value, {
                  status: inferredStatus,
                  is_active: nextIsActive,
                });
              }
            } catch (errStatus) {
              console.error(errStatus);
            }
          }
        }
      }

      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar ausência.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="company-form-component">
      <div className="form-body">
        <h3 className="section-title" style={{ marginTop: 0 }}>
          Dados do lançamento
        </h3>

        <div className="form-grid">
          <div className="full-width">
            <Select
              label="Empresa *"
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setEmployeeId('');
                setServiceStationId('');
                setReasonId('');
              }}
              options={[
                { value: '', label: 'SELECIONE...' },
                ...companyOptions,
              ]}
              searchable
            />
          </div>

          <Select
            label="Posto de serviço"
            value={serviceStationId}
            onChange={(e) => setServiceStationId(e.target.value)}
            options={[
              { value: '', label: 'SELECIONE...' },
              ...stationOptions,
            ]}
            searchable
          />

          <Select
            label="Colaborador *"
            value={employeeId}
            onChange={(e) => {
              const raw = e && e.target !== undefined ? e.target.value : e;
              const value = raw != null && typeof raw === 'object' ? raw.value : raw;
              setEmployeeId(value);
              const emp = employees.find(
                (item) => String(item.value) === String(value)
              );
              if (emp) {
                if (emp.companyId) {
                  setSelectedCompany(String(emp.companyId));
                }
                if (Array.isArray(emp.serviceStationIds) && emp.serviceStationIds.length > 0) {
                  setServiceStationId(emp.serviceStationIds[0]);
                }
              }
            }}
            options={[
              { value: '', label: 'SELECIONE...' },
              ...employeeOptions,
            ]}
            searchable
          />

          <div className="absence-datetime-quad">
            <Input
              label="Data inicial *"
              mask="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              placeholder="dd/mm/aaaa"
            />
            <Input
              label="Hora inicial *"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
            <Input
              label="Data final"
              mask="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="dd/mm/aaaa"
            />
            <Input
              label="Hora final"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>

          <div className="absence-reason-row">
            <Select
              label="Motivo"
              value={reasonId}
              onChange={(e) => setReasonId(e.target.value)}
              options={[
                { value: '', label: 'SELECIONE...' },
                ...reasonOptions,
              ]}
              searchable
            />

            <div>
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                options={[
                  { value: 'registrada', label: 'Registrada' },
                  { value: 'aprovada', label: 'Aprovada' },
                  { value: 'rejeitada', label: 'Rejeitada' },
                  { value: 'rejected_by_substitute', label: 'Rejeitada p/ Substituto' },
                ]}
                searchable={false}
              />
              {initialData && status === 'rejeitada' && (
                <Input
                  label="Motivo da reprovação"
                  value={supervisorRejectionReason}
                  onChange={(e) => setSupervisorRejectionReason(e.target.value)}
                  placeholder="Descreva o motivo"
                />
              )}
              {stageInfo && (
                <div className="manager-tags" style={{ marginTop: 6 }}>
                  <span
                    className={[
                      'manager-tag',
                      stageInfo.variant === 'success' && 'manager-tag-success',
                      stageInfo.variant === 'warning' && 'manager-tag-warning',
                      stageInfo.variant === 'danger' && 'manager-tag-danger',
                      stageInfo.variant === 'info' && 'manager-tag-info',
                      stageInfo.variant === 'neutral' && 'manager-tag-neutral',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {stageInfo.label}
                  </span>
                </div>
              )}
            </div>

            <Select
              label="CID"
              value={cidCode}
              onChange={(e) => setCidCode(e.target.value)}
              options={[{ value: '', label: 'SELECIONE...' }, ...cidOptions]}
              searchable
              placeholder="Selecione..."
            />
          </div>

          <Input
            label="Nome do médico"
            value={doctorName}
            onChange={(e) => setDoctorName(e.target.value)}
            placeholder="Nome completo do médico"
          />

          <Input
            label="CRM do médico"
            value={doctorCrm}
            onChange={(e) => setDoctorCrm(e.target.value)}
            placeholder="Número do CRM"
          />
        </div>

        <h3 className="section-title">Dados do substituto</h3>
        <div className="form-grid absence-substitute-row">
          <Select
            label="Haverá substituto?"
            value={hasSubstitute}
            onChange={(e) => {
              const value = e.target.value;
              setHasSubstitute(value);
              if (value !== 'yes') {
                setSubstituteEmployeeId('');
                setSubstituteEmployee('');
                setSubstituteIsExtra('');
                setSubstituteDayValue('');
                setSubstituteAdvanceValue('');
                setSubstituteVaDayValue('');
                setSubstituteVtDayValue('');
                setSubstituteInvoiceValue('');
              }
            }}
            options={[
              { value: 'no', label: 'NÃO' },
              { value: 'yes', label: 'SIM' },
            ]}
            searchable={false}
          />
          {hasSubstitute === 'yes' && (
            <>
              <Select
                label="Substituto"
                value={substituteEmployeeId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSubstituteEmployeeId(value);
                  const emp = employees.find(
                    (item) => String(item.value) === String(value)
                  );
                  if (emp) {
                    setSubstituteEmployee(emp.label || emp.name || '');
                  }
                }}
                options={[
                  { value: '', label: 'SELECIONE...' },
                  ...employeeOptions.filter(
                    (item) => String(item.value) !== String(employeeId)
                  ),
                ]}
                searchable
              />
              <Select
                label="É extra?"
                value={substituteIsExtra}
                onChange={(e) => setSubstituteIsExtra(e.target.value)}
                options={[
                  { value: '', label: 'NÃO' },
                  { value: 'yes', label: 'SIM' },
                ]}
                searchable={false}
              />
            </>
          )}
        </div>

        {hasSubstitute === 'yes' && substituteIsExtra === 'yes' && (
          <div className="form-grid absence-substitute-values-row" style={{marginTop: '20px'}}>
            <Input
              label="Valor do dia"
              mask="currency"
              value={substituteDayValue}
              onChange={(e) => setSubstituteDayValue(e.target.value)}
              placeholder="0,00"
            />
            <Input
              label="Valor adiantamento"
              mask="currency"
              value={substituteAdvanceValue}
              onChange={(e) => setSubstituteAdvanceValue(e.target.value)}
              placeholder="0,00"
            />
            <Input
              label="Valor VA dia"
              mask="currency"
              value={substituteVaDayValue}
              onChange={(e) => setSubstituteVaDayValue(e.target.value)}
              placeholder="0,00"
            />
            <Input
              label="Valor VT dia"
              mask="currency"
              value={substituteVtDayValue}
              onChange={(e) => setSubstituteVtDayValue(e.target.value)}
              placeholder="0,00"
            />
            <Input
              label="Valor faturar"
              mask="currency"
              value={substituteInvoiceValue}
              onChange={(e) => setSubstituteInvoiceValue(e.target.value)}
              placeholder="0,00"
            />
          </div>
        )}

        <h3 className="section-title">Observações</h3>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
          <Input
            label="Observações gerais"
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            placeholder="Descreva detalhes da ausência, atestados, justificativas, etc."
            multiline
            rows={3}
          />
        </div>

        <h3 className="section-title">Anexos do lançamento</h3>
        <div className="attachments-wrapper">
          <label htmlFor="absence-files" className="attachment-button">
            <FiUploadCloud /> Adicionar anexo
            <input
              id="absence-files"
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </label>

          {(attachments.length > 0 || existingAttachments.length > 0) && (
            <div className="attachments-list">
              {existingAttachments.map((file) => {
                const rawData = file.file_data || '';
                const isDataUrl =
                  typeof rawData === 'string' && rawData.startsWith('data:');
                const href = isDataUrl
                  ? rawData
                  : `data:${file.file_type || 'application/octet-stream'};base64,${rawData}`;

                return (
                  <div
                    key={`existing-${file.id}`}
                    className="attachment-item"
                  >
                    <span className="attachment-name">{file.file_name}</span>
                    <div className="attachment-actions">
                      <a
                        href={href}
                        download={file.file_name}
                        className="download-attachment-btn"
                        title="Baixar"
                      >
                        <FiDownload size={14} />
                      </a>
                      <button
                        type="button"
                        className="remove-attachment-btn"
                        onClick={() =>
                          handleRemoveExistingAttachment(file.id)
                        }
                        title="Excluir"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {attachments.map((file, index) => (
                <div
                  key={`new-${index}`}
                  className="attachment-item"
                >
                  <span className="attachment-name">{file.name}</span>
                  <button
                    type="button"
                    className="remove-attachment-btn"
                    onClick={() => removeAttachment(index)}
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {initialData && (
          <>
            <h3 className="section-title">Assinaturas</h3>
            <div className="absence-signatures-grid">
              <div className="absence-signature-card">
                <div className="absence-signature-label">Colaborador</div>
                {employeeSignatureSrc ? (
                  <img
                    className="absence-signature-preview"
                    src={employeeSignatureSrc}
                    alt="Assinatura do colaborador"
                  />
                ) : (
                  <div className="absence-signature-empty">Sem assinatura</div>
                )}
              </div>

              {(initialData?.substitute_employee_id ||
                initialData?.substitute_employee) && (
                <div className="absence-signature-card">
                  <div className="absence-signature-label">Substituto</div>
                  {substituteSignatureSrc ? (
                    <img
                      className="absence-signature-preview"
                      src={substituteSignatureSrc}
                      alt="Assinatura do substituto"
                    />
                  ) : (
                    <div className="absence-signature-empty">Sem assinatura</div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="modal-actions">
        <Button type="button" className="cancel-button" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
